import { useState } from "react";
import { useAxios } from "../../../utils/useAxios";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";

/**
 * Custom hook to change the status (restore/delete) of a sub-school
 */
export const useChangeSubSchoolStatus = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { axiosInstance } = useAxios();

  /**
   * Changes status of a sub-school by ID
   * @param {string | number} schoolId - The ID of the sub-school
   * @param {string} action - 'delete' or 'restore'
   * @param {string} reason - The reason for the status change
   * @param {(message: string) => void} [onSuccess] - Callback function to be called on success
   * @returns {Promise<boolean>} - Returns true on success, false on failure
   */
  const changeSubSchoolStatus = async (schoolId, action, reason, onSuccess) => {
    try {
      setLoading(true);
      setError("");

      const response = await axiosInstance.patch(
        `${TENANT_DOMAIN}/organization/admin/sub-school/deletion/${schoolId}`,
        {
          action,
          reason,
        }
      );

      if (onSuccess) {
        onSuccess(response.data?.message || `School ${action}d successfully`);
      }

      return true;
    } catch (err) {
      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        const errData = err?.response?.data;
        if (errData?.errors && Object.keys(errData.errors).length > 0) {
          const firstErrorKey = Object.keys(errData.errors)[0];
          setError(errData.errors[firstErrorKey][0]);
        } else {
          setError(
            errData?.message ||
            err?.message ||
            "An unexpected error occurred. Please try again."
          );
        }
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    changeSubSchoolStatus,
    loading,
    error,
    setError,
  };
};
